import type { Rates, Simulation, Role } from '../domain/types';

export interface SimulationInput {
  baseIdleHours: number;
  baseFuelLitres: number;
  baseTonnes: number;
  reductionPct: number;
  litresPerIdleHour: number;
  rates: Rates;
}

export interface SimulationOutput {
  avoidedIdleHours: number;
  fuelSavedLitres: number;
  nokSaved: number;
  co2SavedKg: number;
  newFuelLitres: number;
  newLitresPerTonne: number | null;
  baseLitresPerTonne: number | null;
}

/**
 * Tiltakssimulering «mindre tomgang».
 * Forutsetning: unngått tomgang blir motor av (ikke mer produktiv motortid).
 * Produksjon og produktive timer er uendret. Kun drivstoffeffekten vises som mulig besparelse.
 * Historiske målinger endres aldri.
 */
export function simulateIdleReduction(input: SimulationInput): SimulationOutput {
  const pct = Math.min(30, Math.max(0, input.reductionPct));
  const avoidedIdleHours = input.baseIdleHours * (pct / 100);
  const fuelSavedLitres = avoidedIdleHours * input.litresPerIdleHour;
  const nokSaved = fuelSavedLitres * input.rates.dieselNokPerLitre;
  const co2SavedKg = fuelSavedLitres * input.rates.co2KgPerLitre;
  const newFuelLitres = input.baseFuelLitres - fuelSavedLitres;
  return {
    avoidedIdleHours,
    fuelSavedLitres,
    nokSaved,
    co2SavedKg,
    newFuelLitres,
    newLitresPerTonne: input.baseTonnes > 0 ? newFuelLitres / input.baseTonnes : null,
    baseLitresPerTonne: input.baseTonnes > 0 ? input.baseFuelLitres / input.baseTonnes : null,
  };
}

export function buildSimulationRecord(args: {
  id: string;
  scenarioId: string;
  seedVersion: string;
  deviationId?: string;
  fromDate: string;
  toDate: string;
  input: SimulationInput;
  output: SimulationOutput;
  savedAt: string;
  savedBy: string;
  role: Role;
}): Simulation {
  return {
    id: args.id,
    scenarioId: args.scenarioId,
    seedVersion: args.seedVersion,
    deviationId: args.deviationId,
    basePeriod: { fromDate: args.fromDate, toDate: args.toDate },
    inputAssumptions: {
      reductionPct: args.input.reductionPct,
      litresPerIdleHour: args.input.litresPerIdleHour,
      baseIdleHours: args.input.baseIdleHours,
      baseFuelLitres: args.input.baseFuelLitres,
      baseTonnes: args.input.baseTonnes,
      dieselNokPerLitre: args.input.rates.dieselNokPerLitre,
      co2KgPerLitre: args.input.rates.co2KgPerLitre,
    },
    derivedResults: {
      avoidedIdleHours: args.output.avoidedIdleHours,
      fuelSavedLitres: args.output.fuelSavedLitres,
      nokSaved: args.output.nokSaved,
      co2SavedKg: args.output.co2SavedKg,
      newFuelLitres: args.output.newFuelLitres,
      newLitresPerTonne: args.output.newLitresPerTonne,
    },
    savedAt: args.savedAt,
    savedBy: args.savedBy,
    role: args.role,
  };
}
